import React, { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";

interface InvoiceIncomingStatusToolbarProps {
  currentStatusId: string | null;
  /**
   * Zašto se odluka više ne može mijenjati — javljena provideru ili fiskalizirana kod Porezne.
   * Prazno znači da je odluka još otvorena.
   */
  lockedReason?: string | null;
  onStatusChange?: (statusId: string) => void;
  onPayClick?: () => void;
}

const InvoiceIncomingStatusToolbar: React.FC<InvoiceIncomingStatusToolbarProps> = ({
  currentStatusId,
  lockedReason,
  onStatusChange,
  onPayClick,
}) => {
  const isRejected = currentStatusId === "rejected";
  const isPaid = currentStatusId === "paid";
  const isPartiallyPaid = currentStatusId === "partially_paid";
  const isApproved = currentStatusId === "approved" || isPaid || isPartiallyPaid;
  const isReceived = currentStatusId === "received" || isApproved;

  // Plaćanje tab — spaja paid i partially_paid
  const paymentDone = isPaid;
  const paymentPartial = isPartiallyPaid;

  const canPay = isApproved && !isRejected;

  const steps = [
    {
      id: "received",
      label: "Zaprimljen",
      done: isReceived && !isRejected,
      onClick: () => onStatusChange?.("received"),
    },
    {
      id: "approved",
      label: "Odobren",
      done: isApproved && !isRejected,
      onClick: () => onStatusChange?.("approved"),
    },
    {
      id: "payment",
      label: paymentDone ? "Plaćen" : paymentPartial ? "Djelomično plaćen" : "Plaćanje",
      done: paymentDone,
      partial: paymentPartial,
      onClick: () => canPay && onPayClick?.(),
      disabled: !canPay,
    },
  ];

  // Pronađi zadnji done index
  const lastDoneIndex = steps.reduce((acc, s, i) => (s.done ? i : acc), -1);

  return (
    <div className="mb-4">
    <div className="flex border border-slate-200 rounded-sm overflow-hidden">
      {steps.map((step, index) => {
        const isDone = step.done;
        const isLastDone = index === lastDoneIndex;
        const isPast = isDone && !isLastDone;
        const isNext = !isRejected && !isDone && !step.partial && index === lastDoneIndex + 1;
        // Javljena odluka se ne mijenja klikom — poslužitelj bi je ionako odbio, pa se ovdje
        // zaključava uz objašnjenje. Plaćanje ostaje otvoreno, ono s odlukom nema veze.
        const isDecisionStep = step.id === "received" || step.id === "approved";
        const isDisabled = isRejected || step.disabled || (!!lockedReason && isDecisionStep);

        return (
          <button
            key={step.id}
            type="button"
            disabled={isDisabled}
            title={!!lockedReason && isDecisionStep ? lockedReason : undefined}
            onClick={() => !isDisabled && step.onClick?.()}
            className={classNames(
              "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm transition-all duration-200 border-r last:border-r-0",
              isRejected
                ? "bg-white text-slate-400 border-slate-200 font-medium"
                : step.partial
                ? "bg-amber-500 text-white border-amber-500 font-medium cursor-pointer hover:bg-amber-600"
                : isDone
                ? "bg-green-100 text-green-800 border-b-2 border-b-green-600 font-semibold"
                : isNext && !isDisabled
                ? "bg-sky-50 text-sky-700 border-slate-200 font-medium cursor-pointer"
                : "bg-white text-slate-400 border-slate-200 font-medium",
            )}
          >
            {isDone && (
              <ReactSVG
                src="/media/icons/check.svg"
                className="w-4 h-4 stroke-green-700"
              />
            )}
            <span>{step.label}</span>
          </button>
        );
      })}

      {/* Rejected overlay */}
      {isRejected && (
        <div className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm bg-red-600 text-white border-r-0 font-medium">
          <ReactSVG src="/media/icons/close.svg" className="w-4 h-4 fill-white" />
          <span>Odbijen</span>
        </div>
      )}
    </div>

      {lockedReason && (
        <div className="mt-1.5 text-xs text-slate-500">{lockedReason}</div>
      )}
    </div>
  );
};

export default memo(InvoiceIncomingStatusToolbar);
